import type { FunctionComponent, ReactNode } from 'react'

import { useMediaQuery, useTheme } from '@material-ui/core'
import { FlowPage } from '@monthio/design-system'
import { HomeBudget } from '@monthio/icons'
import { BodyFlow, COLORS, Icons } from '@monthio/react-components'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import type { WhiteLabelSubdomainInfo } from '../../api/budgetApiClient'

import { useLocalStorage } from '../../hooks/useLocalStorage'
import { BudgetHelperFaq } from './BudgetHelperFaq'
import { QUESTIONNAIRE_INPUTS_STORAGE_KEY } from './BudgetHelperFlow'
import {
	ContentWrapper,
	H1WithoutMargin,
	H2WithoutMargin,
	IconAndItemWrapper,
	TitleAndListWrapper,
} from './budgetHelperLandingPageStyles'
import { BudgetHelperPageLayout } from './BudgetHelperPageLayout'
import { StepKey } from './steps/steps'

const BlueBanner = styled.div`
	width: 100%;
	height: 172px;
	background: ${COLORS.BRAND_COLORS.DARK_BLUE};
	left: 0;
	position: absolute;
`

export const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 32px;
`

export const IconWrapper = styled.div`
	z-index: 4;
`

const FlowContainerChoice: FunctionComponent<{
	handleStart: () => void
	children: ReactNode
}> = ({ handleStart, children }) => {
	const { t } = useTranslation()
	const isLarge = useMediaQuery(useTheme().breakpoints.up('sm'))

	const props = {
		title: t('budgetHelper-landingPage-title'),
		subtitle: t('budgetHelper-landingPage-body'),
		icon: (
			<IconWrapper>
				<HomeBudget width={isLarge ? 160 : 87} height={isLarge ? 160 : 87} />
			</IconWrapper>
		),
	}

	return (
		<>
			{isLarge ? <BlueBanner /> : null}
			<FlowPage.Content $spacing="l">
				<FlowPage.Header
					slot={props.icon}
					title={props.title}
					description={props.subtitle}
				/>
				{children}
			</FlowPage.Content>
			<FlowPage.Footer
				actions={
					<FlowPage.Cta onClick={handleStart}>{t('btn-start')}</FlowPage.Cta>
				}
			/>
		</>
	)
}

export const BudgetHelperLandingPage: FunctionComponent<{
	subdomainInfo: WhiteLabelSubdomainInfo
	consumerInputsStepIndex?: number
}> = ({ subdomainInfo, consumerInputsStepIndex }) => {
	const [, , removeValue] = useLocalStorage(
		QUESTIONNAIRE_INPUTS_STORAGE_KEY,
		'',
	)
	const history = useHistory()
	const { t } = useTranslation()
	const isLarge = useMediaQuery(useTheme().breakpoints.up('sm'))

	const handleStart = useCallback(() => {
		removeValue()
		history.push(`/budget-helper/questionnaire/${StepKey.Cohabiting}`, {})
	}, [history])

	return (
		<BudgetHelperPageLayout
			subdomainInfo={subdomainInfo}
			consumerInputsStepIndex={consumerInputsStepIndex}
		>
			<FlowContainerChoice handleStart={handleStart}>
				<Container>
					<ContentWrapper>
						<H2WithoutMargin>
							{t('budgetHelper-landingPage-expectedTime')}
						</H2WithoutMargin>
						<TitleAndListWrapper>
							<H1WithoutMargin>
								{t('budgetHelper-landingPage-requirements')}
							</H1WithoutMargin>
							<IconAndItemWrapper>
								<Icons.CheckCircleIcon fontSize={24} />
								<BodyFlow>
									{t('budgetHelper-landingPage-requirements-mitId')}
								</BodyFlow>
							</IconAndItemWrapper>
						</TitleAndListWrapper>
					</ContentWrapper>

					<div>
						<H1WithoutMargin>
							{t('budgetHelper-landingPage-faqSection-title')}
						</H1WithoutMargin>
						<BudgetHelperFaq />
					</div>
				</Container>
			</FlowContainerChoice>
		</BudgetHelperPageLayout>
	)
}
